/**
 * Google Maps JS API loader — uses the official inline bootstrap that
 * synchronously defines `google.maps.importLibrary`. This avoids the race
 * where a plain `<script>` tag's `onload` fires before `importLibrary`
 * exists.
 *
 * Pattern from: https://developers.google.com/maps/documentation/javascript/load-maps-js-api
 */

let loadPromise = null;

function runBootstrap(apiKey) {
  // The official Google Maps inline bootstrap (minified by Google).
  // It synchronously defines window.google.maps.importLibrary which queues
  // library requests until the real Maps JS bundle finishes loading.
  // eslint-disable-next-line
  (g => { var h, a, k, p = "The Google Maps JavaScript API", c = "google", l = "importLibrary", q = "__ib__", m = document, b = window; b = b[c] || (b[c] = {}); var d = b.maps || (b.maps = {}), r = new Set(), e = new URLSearchParams(), u = () => h || (h = new Promise(async (f, n) => { await (a = m.createElement("script")); e.set("libraries", [...r] + ""); for (k in g) e.set(k.replace(/[A-Z]/g, t => "_" + t[0].toLowerCase()), g[k]); e.set("callback", c + ".maps." + q); a.src = `https://maps.${c}apis.com/maps/api/js?` + e; d[q] = f; a.onerror = () => h = n(Error(p + " could not load.")); a.nonce = m.querySelector("script[nonce]")?.nonce || ""; m.head.append(a); })); d[l] ? console.warn(p + " only loads once. Ignoring:", l) : d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)); })({ key: apiKey, v: "weekly" });
}

export function loadGoogleMaps() {
  if (loadPromise) return loadPromise;
  if (typeof window === "undefined") {
    loadPromise = Promise.reject(new Error("No window"));
    return loadPromise;
  }
  if (window.google?.maps?.places?.AutocompleteSuggestion) {
    loadPromise = Promise.resolve(window.google);
    return loadPromise;
  }

  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) {
    loadPromise = Promise.reject(new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY"));
    return loadPromise;
  }

  loadPromise = (async () => {
    // If importLibrary isn't installed yet, run Google's inline bootstrap.
    if (!window.google?.maps?.importLibrary) {
      runBootstrap(key);
    }
    // Now safely await the places library.
    await window.google.maps.importLibrary("places");
    return window.google;
  })();
  return loadPromise;
}

/**
 * Load additional Maps JS libraries (e.g. "maps", "drawing", "geometry").
 * Safe to call multiple times — `importLibrary` is idempotent.
 */
export async function loadMapsLibraries(libs = []) {
  const g = await loadGoogleMaps();
  for (const lib of libs) {
    // eslint-disable-next-line no-await-in-loop
    await g.maps.importLibrary(lib);
  }
  return g;
}
